"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'

export default function NewShuntingRequestPage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'stationId',
      label: t('shunting.fields.stationId', 'Station'),
      type: 'text',
      required: true,
      placeholder: 'Station UUID',
    },
    {
      id: 'slotId',
      label: t('shunting.fields.slotId', 'Terminal Slot'),
      type: 'text',
      required: true,
      placeholder: 'Slot UUID',
    },
    {
      id: 'requestType',
      label: t('shunting.fields.requestType', 'Request Type'),
      type: 'select',
      required: true,
      options: [
        { value: 'arrival_shunting', label: t('shunting.type.arrival', 'Arrival Shunting') },
        { value: 'departure_shunting', label: t('shunting.type.departure', 'Departure Shunting') },
        { value: 'repositioning', label: t('shunting.type.repositioning', 'Repositioning') },
        { value: 'coupling', label: t('shunting.type.coupling', 'Coupling') },
        { value: 'decoupling', label: t('shunting.type.decoupling', 'Decoupling') },
      ],
    },
    {
      id: 'providerId',
      label: t('shunting.fields.providerId', 'Shunting Provider'),
      type: 'text',
      placeholder: 'Provider UUID (optional)',
    },
    {
      id: 'fromTrack',
      label: t('shunting.fields.fromTrack', 'From Track'),
      type: 'number',
    },
    {
      id: 'toTrack',
      label: t('shunting.fields.toTrack', 'To Track'),
      type: 'number',
    },
    {
      id: 'trainPlanId',
      label: t('shunting.fields.trainPlanId', 'Train Plan'),
      type: 'text',
      placeholder: 'Train Plan UUID (optional)',
    },
    {
      id: 'scheduledAt',
      label: t('shunting.fields.scheduledAt', 'Scheduled At'),
      type: 'datetime-local',
    },
    {
      id: 'notes',
      label: t('shunting.fields.notes', 'Notes'),
      type: 'textarea',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'location',
      title: t('shunting.groups.location', 'Location & Slot'),
      fields: ['stationId', 'slotId'],
    },
    {
      id: 'maneuver',
      title: t('shunting.groups.maneuver', 'Maneuver Details'),
      fields: ['requestType', 'fromTrack', 'toTrack'],
    },
    {
      id: 'assignment',
      title: t('shunting.groups.assignment', 'Assignment'),
      fields: ['providerId', 'trainPlanId', 'scheduledAt'],
    },
    {
      id: 'additional',
      title: t('shunting.groups.additional', 'Additional'),
      fields: ['notes'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('shunting.create.title', 'New Shunting Request')}
          backHref="/backend/shunting-requests"
          fields={fields}
          groups={groups}
          submitLabel={t('shunting.actions.create', 'Create Request')}
          cancelHref="/backend/shunting-requests"
          onSubmit={async (values) => {
            const payload = {
              ...values,
              organizationId,
              tenantId,
            }
            const { result: created } = await createCrud<{ id?: string }>('shunting/shunting-requests', payload)
            flash(t('shunting.flash.created', 'Shunting request created'), 'success')
            router.push(`/backend/shunting-requests/${created?.id}`)
          }}
        />
      </PageBody>
    </Page>
  )
}
